import React from "react";
import { Flex, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "#utils/spacing";
import { WorkflowComment } from "../../types";
import * as styles from "./styles_vanilla.css";
import { formatCommentDate, getInitials, renderCommentBodyHtml } from "./utils";

type CommentCardProps = {
  comment: WorkflowComment;
  isGPX?: boolean;
};

export const CommentCard: React.FC<CommentCardProps> = ({ comment, isGPX = false }) => {
  const isInternal = comment.isInternal;

  const cardClassName = styles.card;

  const renderedHtml = renderCommentBodyHtml(comment.body);

  return (
    <div
      className={cardClassName}
      data-testid={`workflow-comment-${comment.id}`}
      data-internal={isInternal ? "true" : "false"}
    >
      {/* "in [task]" scope label */}
      {comment.subject && (
        <Text
          variant="secondary-sm"
          color={Colors.Neutral600}
          className={styles.commentSubjectLabel}
        >
          in {comment.subject}
        </Text>
      )}

      <Flex flexDirection="row" alignItems="flex-start" gap={SPACING.SMALL}>
        <div className={styles.avatarFallback} aria-hidden="true">
          {getInitials(comment.author.name)}
        </div>
        <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.commentCardBody}>
          {/* Author name + visibility pill + timestamp on one line */}
          <Flex alignItems="center" gap={SPACING.SMALL}>
            <Text variant="strong-sm" color={Colors.Neutral900}>
              {comment.author.name}
            </Text>
            {!isGPX && (
              <span
                className={styles.visibilityPillVariants[isInternal ? "internal" : "external"]}
                data-testid={`workflow-comment-${comment.id}-visibility`}
              >
                {isInternal ? "Internal" : "External"}
              </span>
            )}
            {!comment.isInternal && comment.author.role && (
              <Text
                variant="secondary-sm"
                color={Colors.Neutral600}
                data-testid={`workflow-comment-${comment.id}-role`}
              >
                {comment.author.role}
              </Text>
            )}
            <Text variant="secondary-sm" color={Colors.Neutral600}>
              {formatCommentDate(comment.createdAt)}
            </Text>
          </Flex>
          <div
            className={styles.commentBody}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
            data-testid={`workflow-comment-${comment.id}-body`}
          />
        </Flex>
      </Flex>
    </div>
  );
};
